// src/components/health/timeline/TimelineEvent.tsx
// Individual Timeline Event Component

import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { 
  Zap, 
  Pill, 
  Heart, 
  Thermometer, 
  Moon,
  Activity,
  Calendar,
  FileText,
  ChevronRight,
} from 'lucide-react';
import type { TimelineEvent as TimelineEventType } from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface TimelineEventProps {
  event: TimelineEventType;
  onClick?: () => void;
  highlightSevere?: boolean;
  index?: number;
}

// ============================================================================
// Component
// ============================================================================

export const TimelineEvent: React.FC<TimelineEventProps> = ({
  event,
  onClick,
  highlightSevere = true,
  index = 0,
}) => {
  const config = getEventConfig(event.category);
  const IconComponent = config.icon;
  const isSevere = highlightSevere && event.severity !== undefined && event.severity >= 7;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      onClick={onClick}
      className={`
        relative flex items-start gap-3 p-4 rounded-xl
        bg-white border
        ${isSevere 
          ? 'border-terracotta/30 shadow-sm ring-1 ring-terracotta/10' 
          : 'border-cream-dark'
        }
        ${onClick ? 'cursor-pointer hover:shadow-soft hover:border-sage-light' : ''}
        transition-all duration-200
        group
      `}
    >
      {/* Event Icon */}
      <div
        className={`
          flex-shrink-0 w-10 h-10 rounded-lg
          flex items-center justify-center
          ${config.bgClass}
        `}
      >
        <IconComponent className={`w-5 h-5 ${config.iconClass}`} />
      </div>

      {/* Event Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="font-medium text-navy text-sm">
              {event.title}
            </h4>
            {event.description && (
              <p className="text-sm text-warm-gray mt-0.5 line-clamp-2">
                {event.description}
              </p>
            )}
          </div>

          {/* Severity Indicator */}
          {event.severity !== undefined && (
            <SeverityBadge severity={event.severity} />
          )}
        </div>

        {/* Meta Info */}
        <div className="flex items-center gap-3 mt-2 text-xs text-warm-gray">
          <span>{format(event.timestamp, 'h:mm a')}</span>
          <span className="w-1 h-1 rounded-full bg-warm-gray/50" />
          <span className="capitalize">{event.category.replace('_', ' ')}</span>
        </div>
      </div>

      {/* Hover Arrow */}
      {onClick && (
        <ChevronRight className="
          w-4 h-4 text-warm-gray flex-shrink-0
          opacity-0 group-hover:opacity-100
          transition-opacity duration-200
        " />
      )}

      {/* Severe Indicator */}
      {isSevere && (
        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-terracotta" />
      )}
    </motion.div>
  );
};

// ============================================================================
// Severity Badge
// ============================================================================

interface SeverityBadgeProps {
  severity: number;
}

const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
  const getConfig = () => {
    if (severity >= 8) {
      return {
        label: 'High',
        bgClass: 'bg-red-100',
        textClass: 'text-red-700',
      };
    }
    if (severity >= 5) {
      return {
        label: 'Medium',
        bgClass: 'bg-terracotta/10',
        textClass: 'text-terracotta',
      };
    }
    return {
      label: 'Low',
      bgClass: 'bg-sage/10',
      textClass: 'text-sage-dark',
    };
  };

  const config = getConfig();

  return (
    <span className={`
      flex-shrink-0 px-2 py-0.5 rounded-full
      text-xs font-medium
      ${config.bgClass} ${config.textClass}
    `}>
      {severity}/10
    </span>
  );
};

// ============================================================================
// Event Configuration
// ============================================================================

interface EventConfig {
  icon: React.FC<{ className?: string }>;
  bgClass: string;
  iconClass: string;
}

function getEventConfig(category: string): EventConfig {
  const configs: Record<string, EventConfig> = {
    seizure: {
      icon: Zap,
      bgClass: 'bg-terracotta/10',
      iconClass: 'text-terracotta',
    },
    medication: {
      icon: Pill,
      bgClass: 'bg-sage/10',
      iconClass: 'text-sage-dark',
    },
    therapy: {
      icon: Heart,
      bgClass: 'bg-sage/10',
      iconClass: 'text-sage-dark',
    },
    symptom: {
      icon: Thermometer,
      bgClass: 'bg-terracotta/10',
      iconClass: 'text-terracotta',
    },
    sleep: {
      icon: Moon,
      bgClass: 'bg-navy/10',
      iconClass: 'text-navy',
    },
    vital: {
      icon: Activity,
      bgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
    },
    appointment: {
      icon: Calendar,
      bgClass: 'bg-purple-100',
      iconClass: 'text-purple-600',
    },
    milestone: {
      icon: FileText,
      bgClass: 'bg-success/10',
      iconClass: 'text-success',
    },
    health_log: {
      icon: Activity,
      bgClass: 'bg-cream',
      iconClass: 'text-warm-gray',
    },
    note: {
      icon: FileText,
      bgClass: 'bg-cream',
      iconClass: 'text-warm-gray',
    },
  };

  return configs[category] || {
    icon: Activity,
    bgClass: 'bg-cream',
    iconClass: 'text-warm-gray',
  };
}

export default TimelineEvent;

